import {FC, Fragment} from 'react';
import {Input} from 'semantic-ui-react';
import {isEmpty, map, omitBy} from 'lodash';
import {Field, MapInput, RadioGroupInput, ValueRenderer, ValueInputRendererProps} from 'apstra-ui-common';

import './AnomalyPropertiesFilterInput.less';

const normalizeValue = (properties: Record<string, string> = {}): Record<string, string> | null => {
  const propertiesClone = omitBy(properties, (value, key) => value === '' || key === '');
  if (isEmpty(propertiesClone)) {
    return null;
  }
  return propertiesClone;
};

type AnomalyPropertiesFilterInputProps = Omit<ValueInputRendererProps, 'value'> & {
  value: {
    properties: Record<string, string>,
    strict: boolean
  }
};

export const AnomalyPropertiesFilterInput: FC<AnomalyPropertiesFilterInputProps> = ({
  name, schema, required, disabled, errors, value: sourceValue, onChange
}) => {
  const value = Object.assign({properties: {}, strict: true}, sourceValue);

  const onPropertiesChange = (properties) => {
    return onChange({...value, properties});
  };

  const onModeChange = (strict) => {
    return onChange({...value, strict});
  };

  return (
    <Field
      className='anomaly-properties-filter-input'
      label={schema?.title ?? name}
      description={schema?.description}
      required={required}
      disabled={disabled}
      errors={errors}
    >
      <MapInput
        value={value.properties}
        schema={{}}
        onChange={onPropertiesChange}
        disabled={disabled}
        buttonText='Add Property'
        noItemsMessage='There are no properties to search for.'
      >
        {({key, value, index, errors, setEntryKey, setEntryValue}) =>
          <Fragment>
            <Field key='key' width={8}>
              <Input
                disabled={disabled}
                placeholder='Property'
                value={key}
                onChange={(e) => setEntryKey(index, e.target.value)}
              />
            </Field>
            <Field key='value' width={8} errors={errors}>
              <Input
                disabled={disabled}
                placeholder='Value'
                value={value}
                onChange={(e) => setEntryValue(index, e.target.value)}
              />
            </Field>
          </Fragment>
        }
      </MapInput>
      <RadioGroupInput
        value={value.strict}
        name={name}
        disabled={disabled}
        schema={{
          type: 'boolean',
          oneOf: [
            {const: true, title: 'All'},
            {const: false, title: 'Any'},
          ],
        }}
        onChange={onModeChange}
      />
    </Field>
  );
};

export const anomalyPropertiesFilterRenderer = new ValueRenderer({
  condition: ({name}) => name === 'properties',
  ValueInput: AnomalyPropertiesFilterInput,
  getQueryPart: ({value: {properties, strict} = {}}) => {
    const normalizedProperties = normalizeValue(properties);
    if (!normalizedProperties) {
      return null;
    }
    return (
      {
        matcher: 'have',
        value: map(normalizedProperties, (value, key) => `${key}="${value}"`).join(strict ? ' and ' : ' or '),
      }
    );
  }
});
